import { BadgeCheck, Plus, Tag, X } from 'lucide-react';
import { useState } from 'react';
import type { IOrderPreview } from '@order-up/models';

/**
 * Promo / coupon-code control (customer checkout).
 *
 * SERVER IS AUTHORITATIVE. The customer types a CODE; the component lifts it up
 * via `onApply` and the parent re-`previewOrder(cartId, tip, redeem, code)`. The
 * applied discount we show is whatever the server echoed in
 * `preview.couponDiscount` — never a client-computed amount. A rejected code
 * surfaces `preview.couponError` inline (e.g. "This code has expired"), and the
 * preview is otherwise unchanged (no discount) so a bad code never blocks checkout.
 *
 * Renders as a collapsed "Add promo code" disclosure so the summary column stays
 * quiet for the majority who have no code; expands to the input on demand and
 * auto-expands when the server rejects a code (the error must be seen to be
 * fixed or removed).
 *
 * SKIPPED ITEMS. An item the restaurant marked "Exclude from discounts" (a gift
 * card, a drink the state forbids discounting) never takes a code. The server
 * names those lines in `skippedItems` on the applied discount and on a
 * NOT_APPLICABLE / MIN_ORDER_NOT_MET refusal, and they are listed here under
 * "These items can't be discounted" — a smaller discount, or a refused one,
 * comes with the reason, as it does on the till.
 *
 * This is a PROMO CODE, NOT a gift card. A gift card is a TENDER, not a
 * discount: it pays part (or all) of the total through a server-side hold and
 * the card is charged the remainder — see `GiftCardTender.tsx`, rendered
 * beside this control (spec 2026-10-08 §4.2).
 */

type CouponError = NonNullable<IOrderPreview['couponError']>;
type CouponDiscount = NonNullable<IOrderPreview['couponDiscount']>;
type SkippedItem = NonNullable<CouponDiscount['skippedItems']>[number];

const money = (cents: number): string => `$${(cents / 100).toFixed(2)}`;

export interface PromoCodeProps {
    /** The code currently applied to the live preview (owned by the parent). */
    appliedCode?: string;
    /** Server-derived applied discount from the live preview, if the code applied. */
    couponDiscount?: CouponDiscount;
    /** Server rejection reason from the live preview, if the code did NOT apply. */
    couponError?: CouponError;
    /** True while the preview is re-running after an apply/remove. */
    pending?: boolean;
    /** Lift the entered code to the parent (which re-previews with it). */
    onApply: (code: string) => void;
    /** Clear the applied code in the parent (re-previews at full price). */
    onRemove: () => void;
}

/** Human-readable copy for each machine rejection reason. */
function errorMessage(err: CouponError): string {
    switch (err.reason) {
        case 'EXPIRED':
            return 'This code has expired.';
        case 'NOT_YET_VALID':
            return "This code isn't active yet.";
        case 'REQUIRES_APPROVAL':
            return "This code can't be applied online.";
        case 'MIN_ORDER_NOT_MET':
            // Skipped items do not count toward the minimum — say so, or a guest
            // with a $55 cart reads "spend at least $50" as a bug.
            if (err.skippedItems?.length) {
                return err.minimumOrderCents != null
                    ? `Spend at least ${money(err.minimumOrderCents)} on items that can be discounted to use this code.`
                    : "Your order doesn't meet this code's minimum on items that can be discounted.";
            }
            return err.minimumOrderCents != null
                ? `Spend at least ${money(err.minimumOrderCents)} to use this code.`
                : "Your order doesn't meet this code's minimum.";
        case 'NOT_APPLICABLE':
            return "This code doesn't apply to your items.";
        case 'INVALID_CODE':
        default:
            return "That code isn't valid.";
    }
}

/** The "Exclude from discounts" lines a code was kept off, each with its price. */
function SkippedItems({ items }: { items?: readonly SkippedItem[] }) {
    if (!items?.length) return null;
    return (
        <div className="mt-2 text-xs text-gray-600" data-testid="promo-skipped-items">
            <p className="font-medium text-gray-700">These items can&apos;t be discounted:</p>
            <ul className="mt-1 space-y-0.5">
                {items.map((item, i) => (
                    <li key={`${item.name}-${i}`} className="flex justify-between gap-3">
                        <span>{item.quantity > 1 ? `${item.quantity}× ${item.name}` : item.name}</span>
                        <span className="tabular-nums">{money(item.cents)}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

export default function PromoCode({
    appliedCode,
    couponDiscount,
    couponError,
    pending,
    onApply,
    onRemove,
}: PromoCodeProps) {
    const [input, setInput] = useState('');
    const [open, setOpen] = useState(false);

    const isApplied = Boolean(appliedCode) && Boolean(couponDiscount);
    // A rejection is only meaningful for the code the parent actually submitted.
    const showError = Boolean(appliedCode) && !couponDiscount && Boolean(couponError);

    // A rejected code must be seen to be fixed or removed — the disclosure is
    // forced open (derived, not synced) while the server reports an error.
    const isOpen = open || showError;

    const submit = () => {
        const code = input.trim().toUpperCase();
        if (!code) return;
        onApply(code);
    };

    if (isApplied && couponDiscount) {
        const amount = (couponDiscount.cents / 100).toFixed(2);
        const skipped = couponDiscount.skippedItems;
        return (
            <div className="rounded-2xl border border-green-200 bg-green-50 p-4 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <BadgeCheck className="h-5 w-5 shrink-0 text-green-700" aria-hidden />
                        <div>
                            <p className="font-semibold text-gray-900">
                                <span className="uppercase tracking-wide">{couponDiscount.code}</span>{' '}
                                <span className="text-green-700">− ${amount}</span>
                            </p>
                            <p className="text-xs text-gray-600">{couponDiscount.name}</p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={() => {
                            setInput('');
                            setOpen(false);
                            onRemove();
                        }}
                        aria-label="Remove promo code"
                        className="flex shrink-0 items-center gap-1 text-xs font-medium text-gray-600 underline hover:text-gray-900"
                    >
                        <X className="h-3.5 w-3.5" aria-hidden /> Remove
                    </button>
                </div>
                <SkippedItems items={skipped} />
            </div>
        );
    }

    return (
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                aria-expanded={isOpen}
                className="flex w-full items-center justify-between gap-3 p-4 text-left transition-colors hover:bg-gray-50"
            >
                <span className="flex items-center gap-2">
                    <Tag className="h-5 w-5 text-gray-700" aria-hidden />
                    <span className="font-semibold text-gray-900">Add promo code</span>
                </span>
                <Plus
                    className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${isOpen ? 'rotate-45' : ''}`}
                    aria-hidden
                />
            </button>
            {isOpen && (
                <div className="px-4 pb-4">
                    <div className="flex items-center gap-2">
                        <input
                            type="text"
                            value={input}
                            onChange={(e) => setInput(e.target.value.toUpperCase())}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    submit();
                                }
                            }}
                            placeholder="Enter code"
                            aria-label="Promo code"
                            autoFocus
                            autoCapitalize="characters"
                            autoCorrect="off"
                            spellCheck={false}
                            maxLength={20}
                            className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm uppercase tracking-wide text-gray-900 placeholder:normal-case placeholder:tracking-normal focus:border-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900/10"
                        />
                        <button
                            type="button"
                            onClick={submit}
                            disabled={pending || input.trim().length < 3}
                            className="shrink-0 rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                            {pending ? 'Applying…' : 'Apply'}
                        </button>
                    </div>
                    {showError && couponError && (
                        <div role="alert">
                            {/* The code, then the reason — never "isn't valid" over a
                                valid code that is expired or skips an item. */}
                            <p className="mt-2 text-xs font-medium text-red-600">
                                <span className="uppercase tracking-wide">{couponError.code}</span> —{' '}
                                {errorMessage(couponError)}
                            </p>
                            <SkippedItems items={couponError.skippedItems} />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
